import { Sha256 } from "@aws-crypto/sha256-js";
import { HttpRequest } from "@smithy/protocol-http";
import { SignatureV4 } from "@smithy/signature-v4";
import { fetchAuthSession } from "aws-amplify/auth";
import { Ioc } from "@core/ioc/ioc";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { AUTH_MODEL } from "@models/auth/IAuthModel";
import { loadCloudConfig } from "./cloudConfig";
import { parsePlanType } from "@models/data/campfirePlanType";
import { ACCOUNT_ROLE } from "@models/data/sharedLinkDto";
import type { UserDataExt } from "@models/data/userDataExt";
import { emptyUserDataExt, parseUserDataExt } from "@models/data/userDataExt";

/**
 * `HttpAdminStorageModel` — web port of the sharing slice of Unity's
 * `AwsAdminStorageModel`: the GraphQL admin API that sits beside Dynamo/S3.
 *
 * Two auth modes exist upstream, and it matters which operation uses which:
 *   1. **ID token.** `SendQuery` POSTs with the raw Cognito ID token in the
 *      `authorization` header (no `Bearer` prefix) — `getSharedLinksPipeline`,
 *      `getAccountPersonByUsername`, and the `anonOperation`s `maxperms`,
 *      `projectstatus`, `projduplicate`, **`invite`** and **`safelist_check`**.
 *      Same shape `HttpCustomerModel` uses for `getAccount`.
 *   2. **SigV4.** `SendSignedGraphqlQuery` signs the POST with anonymous
 *      Cognito identity-pool credentials (service `appsync`). Of the sharing
 *      operations ONLY **`lookup`** uses it (`SendGetUserPlanTypeByEmailRequest`,
 *      `:306-318`). An earlier revision of this file said `invite` needed it
 *      too, which kept email invites stubbed for a month — it does not.
 *
 * The web's `sendSignedAsync` reproduces mode 2 with `@smithy/signature-v4`
 * and tries three credential sources in turn (see its doc). Unity's own comment
 * on the lookup request says the lambda answers "an anonymous user, or an
 * authenticated user", so the ID token is one of them.
 */

/** Port of `ShareeInfoModel` (`Models/AuthModel/UserInfo.cs:11-22`). */
export interface ShareeInfo {
  /** `CampfirePlanType` int; `Unknown` (-1) when the user has no account. */
  readonly planType: number;
  readonly firstName: string;
  readonly lastName: string;
  /** The sharee's OWN account. `null` = no Campfire account for this email. */
  readonly accountId: string | null;
  readonly userId: string | null;
  readonly email: string;
  /** `AccountRole` int (`Enum.Parse` of the `appRole` name, no remapping). */
  readonly accountRole: number;
}

/** `ShareEmailModel` (`SharedLinkData.cs:175-182`) — the invite lambda's payload. */
export interface ShareEmailArgs {
  readonly fromFirstName: string;
  readonly inviteUrl: string;
  readonly inviteeUserId: string | null;
  readonly projectName: string;
  readonly inviteeEmail: string;
}

/**
 * Which credential source last satisfied the SigV4 endpoint, remembered per
 * session so every subsequent `lookup` takes the winning path first.
 */
type SignedAuthMode = "identity-pool-user" | "identity-pool-anonymous" | "id-token";

/** One row of `getSharedLinksPipeline` — a link shared WITH the caller. */
export interface ExternalProjectShare {
  readonly id: string;
  /** Owning account of the shared project. */
  readonly customerId: string;
  readonly projectId: string;
  readonly projectName: string;
  readonly projectAuthor: string;
  readonly projectCreatedAt: string;
  readonly projectModifiedAt: string;
  /** `SharedUserRole` int; the pipeline returns null for legacy → Editor. */
  readonly userRole: number;
}

interface GraphQlResponse<T> {
  readonly data?: T;
  readonly errors?: ReadonlyArray<{ message?: string }>;
}

export class HttpAdminStorageModel {
  private readonly m_config = loadCloudConfig();

  /**
   * Port of `AwsAdminStorageModel.GetExternalProjectShares` — every shared
   * link whose `SharedWithEmail` matches the caller, joined server-side with
   * project metadata. Takes NO arguments: the server resolves the caller
   * from the auth header. This is the ONLY way a cross-account share becomes
   * visible (the caller can't query another account's Dynamo partition).
   *
   * Never throws — an unreachable admin API degrades to "no external
   * shares", exactly like Unity's permissive handling.
   */
  async getExternalProjectSharesAsync(
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<ExternalProjectShare>> {
    const query = `query { getSharedLinksPipeline { ID CustomerID ProjectAuthor ProjectID ProjectName ProjectCreatedAt ProjectModifiedAt UserRole } }`;
    const data = await this.sendAuthenticatedAsync<{
      getSharedLinksPipeline?: ReadonlyArray<Record<string, unknown>> | null;
    }>(query, signal);
    const rows = data?.getSharedLinksPipeline;
    if (!Array.isArray(rows)) return [];

    const shares: ExternalProjectShare[] = [];
    for (const row of rows) {
      const id = str(row.ID);
      const projectId = str(row.ProjectID);
      if (!id || !projectId) continue;
      shares.push({
        id,
        customerId: str(row.CustomerID),
        projectId,
        projectName: str(row.ProjectName),
        projectAuthor: str(row.ProjectAuthor),
        projectCreatedAt: str(row.ProjectCreatedAt),
        projectModifiedAt: str(row.ProjectModifiedAt),
        // `SharedLinkData(JToken)`: a null role on the pipeline means Editor.
        userRole: typeof row.UserRole === "number" ? row.UserRole : 1,
      });
    }
    return shares;
  }

  /**
   * Port of `AwsAdminStorageModel.GetUserData` (`:1200-1292`) — the account
   * profile behind every non-ownership permission the app has: the licence
   * role, presenter eligibility, the kinematics seat and the trial window.
   *
   * ⚠ UNLIKE ITS SIBLINGS ON THIS CLASS, THIS ONE THROWS. Everything else
   * here degrades to `null` because an unreachable admin API should cost you
   * a share row, not the app. A failed role fetch is different: swallowing it
   * would cache the seeded **Reader** as if the server had said so, silently
   * locking a Writer out of their own project with no retry and nothing on
   * screen. `Campfire_Web_CloudData.md:35` — "do not silently swallow
   * failures". The caller keeps the value unresolved and retryable.
   *
   * A successful query for a person who simply has no row resolves to the
   * seeded default — that IS the server's answer, and Unity treats it the
   * same way.
   */
  async getUserDataAsync(
    customerId: string,
    userId: string,
    signal?: AbortSignal,
  ): Promise<UserDataExt> {
    if (!customerId || !userId) return emptyUserDataExt(userId);
    const query = `{ getAccountPersonByUsername(accountid: "${escapeGraphQlString(customerId)}", username: "${escapeGraphQlString(userId)}") { appRole username firstName lastName email personid tutorialStatus hasKinematicsSeat canBePresenter trialEnabled trialEndDate trialStartDate preTrialRole } }`;
    const data = await this.sendAuthenticatedOrThrowAsync<{
      getAccountPersonByUsername?: Record<string, unknown> | null;
    }>(query, signal);
    return parseUserDataExt(userId, data?.getAccountPersonByUsername ?? null);
  }

  /**
   * Port of `AwsAdminStorageModel.GetProjectOwnerPlan`
   * (`anonOperation(operation:"projectstatus")`, `:378-405` / `:930-957`) —
   * the plan of the account that OWNS a shared project. A shared project
   * inherits its owner's plan, which is how a free user gets paid features
   * inside an enterprise project.
   *
   * Returns `null` on any failure; the caller then falls back to
   * IndividualFree, i.e. fails closed to FREE, exactly as upstream does.
   */
  async getProjectOwnerPlanAsync(
    projectCustomerId: string,
    sharedLinkId: string,
    signal?: AbortSignal,
  ): Promise<{ planType: number; ownerEmail: string } | null> {
    if (!projectCustomerId || !sharedLinkId) return null;
    const payload = base64Json({ CustomerId: projectCustomerId, SharedLinkId: sharedLinkId });
    const query = `mutation { anonOperation(operation: "projectstatus", payload: "${payload}") { message succeeded } }`;
    const data = await this.sendAuthenticatedAsync<{
      anonOperation?: { message?: string; succeeded?: boolean } | null;
    }>(query, signal);
    if (data?.anonOperation?.succeeded !== true) return null;
    try {
      const parsed = JSON.parse(data.anonOperation.message ?? "{}") as {
        planType?: unknown;
        PlanType?: unknown;
        ownerEmail?: unknown;
        OwnerEmail?: unknown;
      };
      return {
        planType: parsePlanType(parsed.planType ?? parsed.PlanType),
        ownerEmail: str(parsed.ownerEmail ?? parsed.OwnerEmail),
      };
    } catch {
      return null;
    }
  }

  /**
   * Port of `GetMaxAccountRole` (`anonOperation(operation:"maxperms")`) — the
   * ceiling a sharee's own account license puts on their project role.
   * Returns `null` when unavailable (Unity then skips the clamp).
   */
  async getMaxAccountRoleAsync(
    projectCustomerId: string,
    sharedLinkId: string,
    signal?: AbortSignal,
  ): Promise<{ appRole: string | null; canBePresenter: boolean | null } | null> {
    if (!projectCustomerId || !sharedLinkId) return null;
    const payload = base64Json({ CustomerId: projectCustomerId, SharedLinkId: sharedLinkId });
    const query = `query { anonOperation(operation: "maxperms", payload: "${payload}") { message succeeded } }`;
    const data = await this.sendAuthenticatedAsync<{
      anonOperation?: { message?: string; succeeded?: boolean } | null;
    }>(query, signal);
    if (data?.anonOperation?.succeeded !== true) return null;
    try {
      const parsed = JSON.parse(data.anonOperation.message ?? "{}") as {
        appRole?: unknown;
        canBePresenter?: unknown;
      };
      return {
        appRole: typeof parsed.appRole === "string" ? parsed.appRole : null,
        canBePresenter:
          typeof parsed.canBePresenter === "boolean" ? parsed.canBePresenter : null,
      };
    } catch {
      return null;
    }
  }

  /**
   * Port of `AwsAdminStorageModel.DuplicateProject`
   * (`anonOperation(operation:"projduplicate")`) — the server-side project
   * clone. It is the ONLY way this client can duplicate a project: the copy
   * spans the project row, every scene / entity / asset row and the S3 prefix,
   * and only the lambda can do that in one pass.
   *
   * `newProjectId` is minted by the CALLER, exactly as Unity does
   * (`ProjectDuplicationModel.cs:146`), because the mutation returns before
   * the clone exists — knowing the id up front is what makes it pollable.
   *
   * Unity ignores the response body entirely and polls regardless. We read
   * `succeeded` instead: a rejected request that is polled anyway leaves the
   * grid showing a "Duplicating" card for the full 20-minute timeout with
   * nothing to explain it.
   */
  async duplicateProjectAsync(
    args: {
      sourceCustomerId: string;
      projectId: string;
      newProjectId: string;
      newName: string;
      author: string;
      sharedLinkId?: string | null;
    },
    signal?: AbortSignal,
  ): Promise<{ succeeded: boolean; message: string }> {
    // Field names and order are the lambda's contract
    // (`AwsAdminStorageModel.DuplicateProjectRequest`) — camelCase, unlike
    // every other payload on this endpoint. `sharedLinkId` is sent as null
    // rather than omitted, matching `JsonConvert.SerializeObject`'s defaults.
    const payload = base64Json({
      sourceCustomerId: args.sourceCustomerId,
      author: args.author,
      newName: args.newName,
      projectId: args.projectId,
      newProjectId: args.newProjectId,
      sharedLinkId: args.sharedLinkId ?? null,
    });
    const query = `mutation { anonOperation(operation: "projduplicate", payload: "${payload}") { message succeeded } }`;
    const data = await this.sendAuthenticatedAsync<{
      anonOperation?: { message?: string; succeeded?: boolean } | null;
    }>(query, signal);
    return {
      succeeded: data?.anonOperation?.succeeded === true,
      message: data?.anonOperation?.message ?? "",
    };
  }

  /**
   * Port of `AwsAdminStorageModel.GetUserShareInfoByEmail` (`:868-900`) —
   * `anonOperation(operation:"lookup", payload: "{email}")`. The payload is
   * the RAW email, not base64 JSON like every other operation here.
   *
   * Resolves an email to the Campfire user behind it: plan type, account id,
   * user id, app role and names. This is what the invite guards run on, and
   * it is also how the share roster learns a visited sharee's name.
   *
   * THROWS when the user cannot be resolved — a transport failure, a
   * `succeeded: false`, or a payload that does not parse. Unity wraps every
   * failure in `"Error while looking up user - …"` and the CALLER treats the
   * throw as "this email has no account" (`SharedUsersModel.GetUserInfo`
   * returns an empty `ShareeInfoModel` carrying only the email). Keep that
   * contract: returning `null` here would make an OUTAGE indistinguishable
   * from a stranger, and the caller's fallback is deliberately the same.
   *
   * Unity's own comment on the method is worth carrying: it notes the lookup
   * discloses another user's plan and names to anyone who knows their email,
   * and that management considers the Terms and Conditions to permit it.
   */
  async lookupUserByEmailAsync(email: string, signal?: AbortSignal): Promise<ShareeInfo> {
    const query = `mutation { anonOperation(operation: "lookup", payload: "${escapeGraphQlString(email)}") { message succeeded } }`;
    const data = await this.sendSignedAsync<{
      anonOperation?: { message?: string; succeeded?: boolean } | null;
    }>(query, signal);
    const op = data?.anonOperation;
    if (!op || op.succeeded !== true) {
      throw new Error(`Error while looking up user - ${op?.message ?? "no response"}`);
    }
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(op.message ?? "") as Record<string, unknown>;
    } catch (err) {
      throw new Error(`Error while looking up user - ${(err as Error).message}`);
    }
    // `(AccountRole)Enum.Parse(typeof(AccountRole), appRole)` — a name that
    // is not a member THROWS upstream, and the throw is the "no account"
    // signal, so mirror it rather than defaulting.
    const accountRole = parseAccountRoleName(parsed.appRole);
    if (accountRole === null) {
      throw new Error(`Error while looking up user - unknown appRole ${String(parsed.appRole)}`);
    }
    return {
      planType: parsePlanType(parsed.planType),
      firstName: str(parsed.firstName),
      lastName: str(parsed.lastName),
      accountId: nullableStr(parsed.accountid),
      userId: nullableStr(parsed.userid),
      email,
      accountRole,
    };
  }

  /**
   * Port of `AwsAdminStorageModel.SendEmail` (`:653-700`) —
   * `anonOperation(operation:"invite", payload: base64(ShareEmailModel))`,
   * sent with the ID token like every other `anonOperation` except `lookup`.
   * Property names are the lambda's contract and are PascalCase
   * (`JsonConvert.SerializeObject` of `ShareEmailModel`'s properties).
   *
   * Returns whether the lambda reported success. Unity fires this and
   * forgets; the caller here reads the flag so a failed send can say
   * `Error sending invite to {email}` instead of toasting success over a
   * mail that never left.
   */
  async sendSharingEmailAsync(args: ShareEmailArgs, signal?: AbortSignal): Promise<boolean> {
    const payload = base64Json({
      FromFirstName: args.fromFirstName,
      InviteURL: args.inviteUrl,
      InviteeUserId: args.inviteeUserId,
      ProjectName: args.projectName,
      InviteeEmail: args.inviteeEmail,
    });
    const query = `mutation { anonOperation(operation: "invite", payload: "${payload}") { message succeeded } }`;
    const data = await this.sendAuthenticatedAsync<{
      anonOperation?: { message?: string; succeeded?: boolean } | null;
    }>(query, signal);
    return data?.anonOperation?.succeeded === true;
  }

  /**
   * Port of `AwsAdminStorageModel.EmailShareTest` (`:1398-1413`) —
   * `anonOperation(operation:"safelist_check", payload: base64({CustomerId,
   * Email}))`, answering whether `email` is a member / explicitly-added guest
   * of account `customerId`. Unity's free-inviter branch passes the literal
   * email `"NA"` with the SHAREE's account id, asking the inverse question
   * ("is the current user a guest of theirs") — pass it through unchanged.
   *
   * Any failure reads as `false` (Unity logs the exception and returns the
   * `false` default), i.e. the invite is refused rather than allowed.
   */
  async emailShareTestAsync(customerId: string, email: string, signal?: AbortSignal): Promise<boolean> {
    const payload = base64Json({ CustomerId: customerId, Email: email });
    const query = `mutation { anonOperation(operation: "safelist_check", payload: "${payload}") { message succeeded } }`;
    const data = await this.sendAuthenticatedAsync<{
      anonOperation?: { message?: string; succeeded?: boolean } | null;
    }>(query, signal);
    try {
      const parsed = JSON.parse(data?.anonOperation?.message ?? "{}") as { AccessApproved?: unknown };
      return parsed.AccessApproved === true;
    } catch {
      return false;
    }
  }

  /**
   * Port of `SendSignedGraphqlQuery` (`:773-824`) — a SigV4-signed POST to
   * the AppSync endpoint (service `appsync`, the config region, session token
   * in `x-amz-security-token`), which is how Unity sends `lookup`.
   *
   * THREE credential sources are tried, in an order that starts from what is
   * cheapest and already in hand, and the first that the endpoint accepts is
   * remembered for the session:
   *
   *   1. **The signed-in user's identity-pool credentials** — the same ones
   *      Amplify hands the Dynamo / S3 clients (`fetchAuthSession`), so no
   *      extra round trip.
   *   2. **Anonymous identity-pool credentials** — Unity's exact mode
   *      (`GetAnonymousCredentials`): `GetId` with no logins, then
   *      `GetCredentialsForIdentity`, both unsigned public calls to
   *      `cognito-identity`. Two extra round trips, cached until they expire.
   *   3. **The ID token**, unsigned — the mode every OTHER operation uses;
   *      Unity's comment on the lookup request says the lambda accepts
   *      "an anonymous user, or an authenticated user".
   *
   * "Accepted" means the request got past the authorizer: any 2xx whose
   * GraphQL errors are not authorization errors. A lambda that answers
   * `succeeded: false` has still ANSWERED, and that result is returned rather
   * than retried under another identity — otherwise a genuine "no such user"
   * would cost three round trips and end up looking like an outage.
   */
  private async sendSignedAsync<T>(query: string, signal?: AbortSignal): Promise<T | null> {
    const order: SignedAuthMode[] = ["identity-pool-user", "identity-pool-anonymous", "id-token"];
    if (this.m_signedAuthMode) {
      order.splice(order.indexOf(this.m_signedAuthMode), 1);
      order.unshift(this.m_signedAuthMode);
    }
    let lastError: unknown = null;
    for (const mode of order) {
      try {
        const result = await this.trySendSignedAsync<T>(mode, query, signal);
        if (result.authorized) {
          this.m_signedAuthMode = mode;
          return result.data;
        }
        lastError = result.error;
      } catch (err) {
        if ((err as { name?: string })?.name === "AbortError") return null;
        lastError = err;
      }
    }
    console.warn("[HttpAdminStorageModel] signed GraphQL request refused by every credential source:", lastError);
    return null;
  }

  private async trySendSignedAsync<T>(
    mode: SignedAuthMode,
    query: string,
    signal?: AbortSignal,
  ): Promise<{ authorized: boolean; data: T | null; error?: unknown }> {
    const body = JSON.stringify({ query });
    const url = new URL(this.m_config.graphqlEndpoint);
    let headers: Record<string, string>;

    if (mode === "id-token") {
      const idToken = await Ioc.resolve<IAuthModel>(AUTH_MODEL).getIdToken();
      if (!idToken) return { authorized: false, data: null, error: "no id token" };
      headers = { authorization: idToken, "content-type": "application/json" };
    } else {
      const creds = mode === "identity-pool-user"
        ? await this.userIdentityCredentialsAsync()
        : await this.anonymousIdentityCredentialsAsync(signal);
      if (!creds) return { authorized: false, data: null, error: `${mode}: no credentials` };
      const signer = new SignatureV4({
        service: "appsync",
        region: this.m_config.region,
        credentials: creds,
        sha256: Sha256,
      });
      const request = new HttpRequest({
        method: "POST",
        protocol: url.protocol,
        hostname: url.hostname,
        path: url.pathname,
        headers: { host: url.hostname, "content-type": "application/json" },
        body,
      });
      const signed = await signer.sign(request);
      headers = {};
      for (const [key, value] of Object.entries(signed.headers)) {
        // `host` is set by the browser and is a forbidden request header.
        if (key.toLowerCase() === "host") continue;
        headers[key] = value;
      }
    }

    const response = await fetch(this.m_config.graphqlEndpoint, {
      method: "POST",
      headers,
      body,
      signal,
    });
    if (response.status === 401 || response.status === 403) {
      return { authorized: false, data: null, error: `HTTP ${response.status}` };
    }
    if (!response.ok) {
      return { authorized: false, data: null, error: `HTTP ${response.status} ${response.statusText}` };
    }
    const payload = (await response.json()) as GraphQlResponse<T>;
    if (payload.errors?.length) {
      const messages = payload.errors.map((e) => e.message ?? "").join("; ");
      if (/unauthori[sz]ed|not authorized/i.test(messages)) {
        return { authorized: false, data: null, error: messages };
      }
      console.warn("[HttpAdminStorageModel] signed GraphQL errors:", messages);
    }
    return { authorized: true, data: payload.data ?? null };
  }

  /** Strategy 1 — the signed-in user's Cognito identity-pool credentials. */
  private async userIdentityCredentialsAsync(): Promise<SigV4Credentials | null> {
    try {
      const session = await fetchAuthSession();
      const creds = session.credentials;
      if (!creds?.accessKeyId || !creds.secretAccessKey) return null;
      return {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        sessionToken: creds.sessionToken,
      };
    } catch {
      return null;
    }
  }

  /**
   * Strategy 2 — anonymous (unauthenticated) identity-pool credentials, the
   * mode Unity's `GetAnonymousCredentials` produces. Both calls are public,
   * unsigned JSON-RPC posts to the Cognito Identity endpoint; no SDK client
   * is needed for two operations. Cached until a minute before expiry.
   */
  private async anonymousIdentityCredentialsAsync(signal?: AbortSignal): Promise<SigV4Credentials | null> {
    const cached = this.m_anonymousCredentials;
    if (cached && cached.expiresAt - Date.now() > 60_000) return cached.credentials;
    const endpoint = `https://cognito-identity.${this.m_config.region}.amazonaws.com/`;
    const call = async <R>(target: string, payload: unknown): Promise<R | null> => {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "content-type": "application/x-amz-json-1.1",
          "x-amz-target": `AWSCognitoIdentityService.${target}`,
        },
        body: JSON.stringify(payload),
        signal,
      });
      if (!response.ok) return null;
      return (await response.json()) as R;
    };
    const identity = await call<{ IdentityId?: string }>("GetId", {
      IdentityPoolId: this.m_config.identityPoolId,
    });
    if (!identity?.IdentityId) return null;
    const granted = await call<{
      Credentials?: { AccessKeyId?: string; SecretKey?: string; SessionToken?: string; Expiration?: number };
    }>("GetCredentialsForIdentity", { IdentityId: identity.IdentityId });
    const c = granted?.Credentials;
    if (!c?.AccessKeyId || !c.SecretKey) return null;
    const credentials: SigV4Credentials = {
      accessKeyId: c.AccessKeyId,
      secretAccessKey: c.SecretKey,
      sessionToken: c.SessionToken,
    };
    // `Expiration` is epoch SECONDS (a `Date` in the SDK's shape).
    const expiresAt = typeof c.Expiration === "number" ? c.Expiration * 1000 : Date.now() + 55 * 60_000;
    this.m_anonymousCredentials = { credentials, expiresAt };
    return credentials;
  }

  private m_signedAuthMode: SignedAuthMode | null = null;
  private m_anonymousCredentials: { credentials: SigV4Credentials; expiresAt: number } | null = null;

  /** Authenticated GraphQL POST (raw ID token in `authorization`). */
  /**
   * Same request as {@link sendAuthenticatedAsync} but SURFACES failure. Used
   * only where a swallowed error would be cached as a real answer — see
   * {@link getUserDataAsync}.
   */
  private async sendAuthenticatedOrThrowAsync<T>(
    query: string,
    signal?: AbortSignal,
  ): Promise<T | null> {
    const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
    const idToken = await auth.getIdToken();
    if (!idToken) throw new Error("not authenticated");
    const response = await fetch(this.m_config.graphqlEndpoint, {
      method: "POST",
      headers: {
        authorization: idToken,
        "content-type": "application/json",
      },
      body: JSON.stringify({ query }),
      signal,
    });
    if (!response.ok) {
      throw new Error(`GraphQL HTTP ${response.status} ${response.statusText}`);
    }
    const payload = (await response.json()) as GraphQlResponse<T>;
    if (payload.errors?.length) {
      throw new Error(payload.errors.map((e) => e.message).join("; "));
    }
    return payload.data ?? null;
  }

  private async sendAuthenticatedAsync<T>(
    query: string,
    signal?: AbortSignal,
  ): Promise<T | null> {
    const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
    const idToken = await auth.getIdToken();
    if (!idToken) return null;
    try {
      const response = await fetch(this.m_config.graphqlEndpoint, {
        method: "POST",
        headers: {
          // Raw token, NOT `Bearer …` — matches Unity's `SendQuery` and the
          // AppSync Cognito-pool authorizer.
          authorization: idToken,
          "content-type": "application/json",
        },
        body: JSON.stringify({ query }),
        signal,
      });
      if (!response.ok) {
        console.warn(
          `[HttpAdminStorageModel] GraphQL HTTP ${response.status} ${response.statusText}`,
        );
        return null;
      }
      const payload = (await response.json()) as GraphQlResponse<T>;
      if (payload.errors?.length) {
        console.warn(
          "[HttpAdminStorageModel] GraphQL errors:",
          payload.errors.map((e) => e.message).join("; "),
        );
      }
      return payload.data ?? null;
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") return null;
      console.warn("[HttpAdminStorageModel] GraphQL request failed:", err);
      return null;
    }
  }
}

function escapeGraphQlString(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** A string, or `null` for absent / null / empty — Unity's `Value<string>()` on a null token. */
function nullableStr(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * `Enum.Parse(typeof(AccountRole), name)` — the four member names, nothing
 * else, and NO Presenter→Commenter remap (that remap belongs to
 * `parseUserDataExt`, a different code path). `null` where Unity throws.
 */
function parseAccountRoleName(value: unknown): number | null {
  switch (value) {
    case "Reader": return ACCOUNT_ROLE.Reader;
    case "Writer": return ACCOUNT_ROLE.Writer;
    case "Commenter": return ACCOUNT_ROLE.Commenter;
    case "Presenter": return ACCOUNT_ROLE.Presenter;
    default: return null;
  }
}

interface SigV4Credentials {
  readonly accessKeyId: string;
  readonly secretAccessKey: string;
  readonly sessionToken?: string;
}

/**
 * Unity's `Convert.ToBase64String(Encoding.UTF8.GetBytes(json))`. The UTF-8
 * step is not decoration: `btoa` throws on any code point above U+00FF, and
 * these payloads carry user-authored text (a project NAME reaches
 * `projduplicate` this way), so a bare `btoa` turns an accented or CJK title
 * into an `InvalidCharacterError` at the call site.
 */
function base64Json(value: unknown): string {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export const ADMIN_STORAGE_MODEL = Symbol.for("Campfire.AdminStorageModel");
